import { TEX } from "../core/keys"
import type {
  Character,
  HeroControlProfile,
  PlayerPassives,
  PlayerStats,
} from "../types"

interface HeroAnimationDefinition {
  key: string
  assetPath: string
  frameWidth: number
  frameHeight: number
  frames: number
  frameRate: number
  repeat?: number
}

interface HeroDefinition extends Character {
  id: string
  name: string
  breed: string
  description: string
  flapPower: number
  maxHp: number
  color: string
  accentColor: string
  baseStats?: Partial<PlayerStats>
  basePassives?: Partial<PlayerPassives>
  controlProfile?: HeroControlProfile
  textureKey: string
  assetPath: string
  portraitPath: string
  animation?: HeroAnimationDefinition
}

export const HERO_DEFINITIONS: HeroDefinition[] = [
  {
    id: "bella",
    name: "Bella",
    breed: "Border Collie",
    description: "FIELD CAPTAIN. Built for consistent damage and survival.",
    flapPower: 1.0,
    maxHp: 200,
    color: "#1A1A1A",
    accentColor: "#FFFFFF",
    glowColor: "#F472B6",
    unlocked: true,
    role: "balanced",
    perk: "+15% Ball Speed, longer combo grace.",
    baseStats: { attackSpeed: 3, ballCount: 1, ballBounces: 0, ballSize: 0.4 },
    basePassives: { comboDecayGracePeriod: 1500 },
    controlProfile: {
      redirectStrength: 0.65,
      speedMultiplier: 1.0,
      recoveryDuration: 1400,
      comboAcceleration: 1.0,
      forgiveness: "high",
    },
    textureKey: TEX.CHAR_BELLA,
    assetPath: "characters/heroes/bella/fly-sheet.webp",
    portraitPath: "characters/heroes/bella/bella.webp",
    animation: {
      key: "hero_bella_fly",
      assetPath: "characters/heroes/bella/fly-sheet.webp",
      frameWidth: 256,
      frameHeight: 256,
      frames: 4,
      frameRate: 10,
      repeat: -1,
    },
  },
  {
    id: "krmelo",
    name: "KR-Melo",
    breed: "Stray Dog",
    description:
      "SCAVENGER. Economy powerhouse. Levels fast and vacuums coins, but needs movement discipline to survive.",
    flapPower: 1.25,
    maxHp: 100,
    color: "#D4A574",
    accentColor: "#8B6914",
    glowColor: "#F59E0B",
    unlocked: true,
    role: "speed",
    perk: "+20% Ball Speed, faster recovery.",
    baseStats: { attackSpeed: 6, ballCount: 2, ballBounces: 1, ballSize: 0.6 },
    basePassives: { comboDecayGracePeriod: -400 },
    controlProfile: {
      redirectStrength: 0.55,
      speedMultiplier: 1.12,
      recoveryDuration: 1100,
      comboAcceleration: 1.08,
      forgiveness: "medium",
    },
    textureKey: TEX.CHAR_KRMELO,
    assetPath: "characters/heroes/krmelo/fly-sheet.webp",
    portraitPath: "characters/heroes/krmelo/krmelo.webp",
    animation: {
      key: "hero_krmelo_fly",
      assetPath: "characters/heroes/krmelo/fly-sheet.webp",
      frameWidth: 256,
      frameHeight: 256,
      frames: 4,
      frameRate: 10,
      repeat: -1,
    },
  },
]

export function getHeroAvailabilityLabel(
  hero: Pick<HeroDefinition, "unlocked">,
): "Available" | "Locked" {
  return hero.unlocked ? "Available" : "Locked"
}

export function getHeroGlowColorHex(heroId: string): string {
  const hero = getHeroDefinitionById(heroId)
  return hero.glowColor
}

export function getHeroGlowColor(heroId: string): number {
  const hex = getHeroGlowColorHex(heroId)
  return Number.parseInt(hex.replace("#", ""), 16)
}

export function getHeroDefinitionById(heroId: string): HeroDefinition {
  const hero = HERO_DEFINITIONS.find((entry) => entry.id === heroId)

  if (!hero) {
    throw new Error(`Unknown hero: ${heroId}`)
  }

  return hero
}

function toCharacter({
  textureKey: _textureKey,
  assetPath: _assetPath,
  portraitPath: _portraitPath,
  ...character
}: HeroDefinition): Character {
  return character
}

export const CHARACTERS: Character[] = HERO_DEFINITIONS.map(toCharacter)
